import React, { useState, useEffect, useMemo } from "react";
import { Edit, Form, Input, DatePicker, useForm, Alert, Select, Checkbox, InputNumber, Divider } from "@pankod/refine-antd";
import axios from "@pankod/refine-simple-rest/node_modules/axios";
import { API_URL, TOKEN_KEY } from "../../constants/environment";
import { useNavigation, usePermissions } from "@pankod/refine-core";
import { notification } from "@pankod/refine-antd";
import { IRentPlace, RentPlaceStatusEnum } from "../../interfaces/rent-place.interface";
import { placeOptions, getAllowedPlaces, hasPlacePermission, rentPlaceStatusOptions } from "../../constants/rentPlace.constants";
import { CustomTimePicker } from "../../components/atoms/custom-time-picker";
import dayjs from "dayjs";

export const RentPlaceEdit: React.FC = () => {
    const { formProps, saveButtonProps, queryResult } = useForm<IRentPlace>();
    const record = queryResult?.data?.data;
    const { push } = useNavigation();
    const { data: permissionsData } = usePermissions();
    
    const [startTime, setStartTime] = useState("09:00");
    const [endTime, setEndTime] = useState("10:00");
    const [cost, setCost] = useState<number>(0);
    const [discount, setDiscount] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);

    // Verificar permisos para este lugar específico
    const isAdmin = permissionsData?.includes('rent-places:admin');
    const canEditThisPlace = record ? (isAdmin || hasPlacePermission(record.place || '', permissionsData || [])) : false;
    
    // Verificar si el evento está pagado y si han pasado más de 5 minutos desde el pago
    const isPaidEvent = record?.status === RentPlaceStatusEnum.PAID;
    const canEditPaymentData = useMemo(() => {
        if (!isPaidEvent || !record?.payment_date) return false;
        
        const now = dayjs();
        const paymentDate = dayjs(record.payment_date);
        const minutesSincePayment = now.diff(paymentDate, 'minutes');
        
        return minutesSincePayment <= 5;
    }, [isPaidEvent, record?.payment_date]);
    
    // Filtrar lugares según permisos del usuario
    const allowedPlaces = getAllowedPlaces(permissionsData || []);
    const filteredPlaceOptions = placeOptions.filter(option => 
        isAdmin || allowedPlaces.includes(option.value)
    );
    
    // Actualizar los valores cuando se carga el record
    useEffect(() => {
        if (record?.start_time) {
            setStartTime(record.start_time);
        }
        if (record?.end_time) {
            setEndTime(record.end_time);
        }
        
        // Inicializar datos de pago si existen
        if (record && isPaidEvent) {
            const initialCost = Number(record.rental_cost) || 0;
            const initialDiscount = Number(record.rental_discount) || 0;
            setCost(initialCost);
            setDiscount(initialDiscount);
        }
    }, [record, isPaidEvent]);

    // Calcular total automáticamente
    useEffect(() => {
        const calculatedTotal = Math.max(0, cost - discount);
        setTotal(calculatedTotal);
    }, [cost, discount]);

    const onFinish = async (values: any) => {
        let payload: any = {};
        
        if (isPaidEvent) {
            // Para eventos pagados, solo permitir editar observaciones y needs_invoice siempre
            payload = {
                observations: values.observations || "",
                needs_invoice: values.needs_invoice || false,
            };
            
            // Si pueden editar datos de pago (dentro de 5 minutos)
            if (canEditPaymentData) {
                payload.rental_cost = cost;
                payload.rental_discount = discount;
                payload.payment_comment = values.payment_comment || "";
            }
        } else {
            // Para eventos no pagados, permitir editar todos los campos
            payload = {
                start_date: typeof values.start_date === "string" ? values.start_date : values.start_date.format("YYYY-MM-DD"),
                end_date: typeof values.start_date === "string" ? values.start_date : values.start_date.format("YYYY-MM-DD"),
                start_time: startTime,
                end_time: endTime,
                place: values.place || "",
                receipt_number: values.receipt_number || "",
                institution: values.institution || "",
                event_name: values.event_name || "",
                responsible_name: values.responsible_name || "",
                responsible_phone: values.responsible_phone || "",
                observations: values.observations || "",
                is_free: values.is_free || false,
            };
        }

        const token = localStorage.getItem(TOKEN_KEY);
        try {
            await axios.patch(`${API_URL}/rent-places/${record?.id}`, payload, {
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });
            notification.success({
                message: "Renta de lugar actualizada correctamente",
                description: "El registro fue editado exitosamente.",
            });
            push("/rent-places");
        } catch (error: any) {
            notification.error({
                message: "Error al actualizar",
                description: error?.response?.data?.message || "Ocurrió un error al actualizar la renta de lugar.",
            });
        }
    };

    return (
        <Edit saveButtonProps={saveButtonProps} title={"Editar Renta de Lugar"}>
            {!canEditThisPlace && record && (
                <Alert
                    message="Sin permisos"
                    description={`No tienes permisos para editar eventos del lugar: ${record.place}`}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 24 }}
                />
            )}
            
            {isPaidEvent && (
                <Alert
                    message={canEditPaymentData ? "Evento pagado - Edición limitada" : "Evento pagado - Solo observaciones"}
                    description={
                        canEditPaymentData 
                            ? "Este evento está pagado. Solo puedes editar las observaciones, si requiere factura y los datos de pago (dentro de 5 minutos del pago)."
                            : "Este evento está pagado. Solo puedes editar las observaciones y si requiere factura."
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 24 }}
                />
            )}
            
            <Form {...formProps} layout="vertical" onFinish={onFinish}>
                {!isPaidEvent && (
                    <>
                        <Form.Item
                            name="start_date"
                            label="Fecha de Inicio"
                            rules={[{ required: true }]}
                            getValueProps={(value) => ({
                                value: value
                                ?
                                    (typeof value === "string"
                                    ? require("dayjs")(value, "YYYY-MM-DD", true)
                                    : require("dayjs")(value))
                                : undefined,
                            })}
                        >
                            <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item label="Hora de inicio" rules={[{ required: true }]}>
                            <CustomTimePicker
                                value={startTime}
                                onChange={setStartTime}
                                placeholder="Seleccionar hora de inicio"
                            />
                        </Form.Item>
                        <Form.Item label="Hora de fin" rules={[{ required: true }]}>
                            <CustomTimePicker
                                value={endTime}
                                onChange={setEndTime}
                                placeholder="Seleccionar hora de fin"
                            />
                        </Form.Item>
                        <Form.Item name="place" label="Lugar" rules={[{ required: true }]}>
                            <Select
                                options={filteredPlaceOptions}
                                placeholder="Selecciona un lugar"
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                        <Form.Item name="receipt_number" label="Número de Recibo" rules={[{ required: false }]}>
                            <Input placeholder="Número de recibo (opcional)" />
                        </Form.Item>
                        <Form.Item name="institution" label="Institución" rules={[{ required: false }]}>
                            <Input placeholder="Nombre de la institución (opcional)" />
                        </Form.Item>
                        <Form.Item name="event_name" label="Nombre del Evento" rules={[{ required: true }]}>
                            <Input placeholder="Nombre del evento" />
                        </Form.Item>
                        <Form.Item name="responsible_name" label="Nombre del Responsable" rules={[{ required: true }]}>
                            <Input placeholder="Nombre completo del responsable" />
                        </Form.Item>
                        <Form.Item name="responsible_phone" label="Celular del Responsable" rules={[{ required: true }]}>
                            <Input placeholder="Número de celular" />
                        </Form.Item>
                        <Form.Item name="is_free" valuePropName="checked" label="">
                            <Checkbox>Evento Gratuito</Checkbox>
                        </Form.Item>
                    </>
                )}
                
                {/* Campos siempre editables para eventos pagados */}
                <Form.Item name="observations" label="Observaciones">
                    <Input.TextArea rows={3} placeholder="Observaciones adicionales" />
                </Form.Item>
                
                {isPaidEvent && (
                    <Form.Item name="needs_invoice" valuePropName="checked">
                        <Checkbox>¿Necesita factura?</Checkbox>
                    </Form.Item>
                )}
                
                {/* Campos de pago editables solo dentro de 5 minutos */}
                {isPaidEvent && canEditPaymentData && (
                    <>
                        <Divider>Datos de Pago (Editable por 5 minutos)</Divider>
                        <Form.Item
                            label="Costo de la Renta"
                            rules={[
                                { required: true, message: "El costo es requerido" },
                                { type: "number", min: 0, message: "El costo debe ser mayor o igual a 0" }
                            ]}
                        >
                            <InputNumber
                                style={{ width: "100%" }}
                                placeholder="0.00"
                                prefix="$"
                                precision={2}
                                value={cost}
                                onChange={(value) => setCost(Number(value) || 0)}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Descuento"
                            rules={[
                                { type: "number", min: 0, message: "El descuento debe ser mayor o igual a 0" }
                            ]}
                        >
                            <InputNumber
                                style={{ width: "100%" }}
                                placeholder="0.00"
                                prefix="$"
                                precision={2}
                                value={discount}
                                onChange={(value) => setDiscount(Number(value) || 0)}
                            />
                        </Form.Item>

                        <Form.Item label="Total a Pagar">
                            <InputNumber
                                style={{ 
                                    width: "100%", 
                                    backgroundColor: "#f5f5f5",
                                    fontSize: "16px",
                                    fontWeight: "bold"
                                }}
                                prefix="$"
                                precision={2}
                                value={total}
                                disabled
                            />
                        </Form.Item>

                        <Form.Item name="payment_comment" label="Comentario de Pago (Opcional)">
                            <Input.TextArea
                                rows={2}
                                placeholder="Agregar una nota sobre el pago..."
                                maxLength={500}
                            />
                        </Form.Item>
                    </>
                )}
            </Form>
        </Edit>
    );
};

export default RentPlaceEdit;
